import useApiClient from "hooks/useApiClient";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHotelContext } from "./useHotelContext";
import { useLoadingContext } from "states/loadingContext";
import { toast } from "react-toastify";
import { t } from "i18next";
import useMimir from "hooks/useMimir";

const useCheckRate = (hotel) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { api, urls } = useApiClient();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { setHotelSelection, hotelSearchParams } = useHotelContext();
    const { showLoading, hideLoading } = useLoadingContext();
    const { mimir } = useMimir();

    const buildMimirPayload = (payload, result) => {
        return {
            type: "HOTEL",
            event: "CHECKRATE",
            param1: JSON.stringify({
                ...payload,
                hotelName: hotel.name,
            }),
            param2: JSON.stringify(result || {}),
        };
    };

    const prepareBasePayload = () => {
        const basePayload = {
            checkInDate: hotelSearchParams?.checkInDate,
            checkOutDate: hotelSearchParams?.checkOutDate,
            hotelCode: hotel.code,
            provider: hotel.provider,
            provider_id: hotel.provider_id,
            trace: true,
            language: i18n.language,
        };
        hotel.hotelCodeCrypted &&
            (basePayload["hotelCodeCrypted"] = hotel.hotelCodeCrypted);
        return basePayload;
    };

    const checkRateNotCombined = async (rateKey, selectedRates) => {
        const payload = {
            ...prepareBasePayload(),
        };
        if (rateKey) {
            // this is for cancellation
            payload["rateKey_1"] = rateKey;
            payload["room"] = 1;
        } else {
            // this is for checkrate
            selectedRates?.forEach((rate, index) => {
                payload[`rateKey_${index + 1}`] = rate.rateKey;
            });
            payload["room"] = selectedRates?.length;
        }
        let result;
        try {
            setIsLoading(true);
            const res = await api.post(
                urls.hotel.checkRates,
                JSON.stringify(payload),
            );
            setIsLoading(false);

            if (
                !res.data ||
                res.data?.error ||
                res.data?.hasOwnProperty("IBE_Errors") ||
                res.data?.result?.length === 0
            ) {
                setError(t("tryAgainLater"));
                result = res;
                return null;
            }
            result = res.data?.result;
            return result;
        } catch (error) {
            if (error?.code === "ERR_NETWORK") {
                toast.error("Network error, please try again later.");
                return null;
            }
            toast.error(t("tryAgainLater"));
            setError(t("tryAgainLater"));
            return null;
        } finally {
            setIsLoading(false);
            mimir.log(buildMimirPayload(payload, result));
        }
    };

    const checkRateCombined = async (rateKey, isCancellationPolicy = false) => {
        const payload = {
            ...prepareBasePayload(),
        };
        payload["rateKey_1"] = rateKey;
        payload["room"] = 1;
        payload["isCancellationPolicy"] = isCancellationPolicy;
        let result;
        try {
            setIsLoading(true);
            const res = await api.post(
                urls.hotel.checkRates,
                JSON.stringify(payload),
            );
            if (
                !res.data ||
                res.data?.error ||
                res.data?.hasOwnProperty("IBE_Errors") ||
                res.data?.result?.length === 0
            ) {
                setError(t("tryAgainLater"));
                result = res;
                return null;
            }
            result = res.data?.result;
            return result;
        } catch (error) {
            if (error?.code === "ERR_NETWORK") {
                toast.error("Network error, please try again later.");
            } else {
                setError(t("tryAgainLater"));
            }
            return null;
        } finally {
            setIsLoading(false);
            mimir.log(buildMimirPayload(payload, result));
        }
    };

    const goToDetailsCombined = async (rate) => {
        showLoading();
        checkRateCombined(rate.rateKey)
            .then((res) => {
                if (!res) {
                    setError(t("tryAgainLater"));
                    toast.error("Error while checking rate ");
                    return;
                }
                let cancellationPolicies = [];
                if (res?.cancellationPolicies) {
                    cancellationPolicies =
                        res?.cancellationPolicies?.cancellationPolicies;
                } else if (res?.rooms?.[0]?.rates?.[0]?.cancellationPolicies) {
                    cancellationPolicies =
                        res?.rooms?.[0]?.rates?.[0]?.cancellationPolicies;
                }

                rate.supplements = res?.rooms?.[0]?.supplements || [];

                setHotelSelection({
                    selectedHotel: hotel,
                    cancellationPolicies: cancellationPolicies,
                    comments: res?.comments || [],
                    seletedRates: [rate],
                    checkRateResult: res,
                    totalPrice: res?.totalNet?.total || rate?.price,
                    supplementsArray:
                        res?.rooms?.[0]?.supplements?.filter(
                            (sup) => sup?.SuppIsMandatory,
                        ) || [],
                });
                navigate(`/hotel-details`);
            })
            .catch((err) => {
                console.log(err);
                setError(t("tryAgainLater"));
                toast.error(t("tryAgainLater"));
            })
            .finally(() => {
                hideLoading();
            });
    };
    const goToDetailsNotCombined = async (selectedRates) => {
        if (hotel.callCheckRates) {
            showLoading();
            checkRateNotCombined(null, selectedRates)
                .then((res) => {
                    if (!res) {
                        setError(t("tryAgainLater"));
                        toast.error(t("tryAgainLater"));
                        return;
                    }
                    if (res?.updatedRateKeys) {
                        console.log("update ratekeys");
                        selectedRates.forEach((rate, index) => {
                            rate.rateKey = res?.rooms?.[index]?.rateKey;
                        });
                    }
                    let cancellationPolicies = [];
                    if (res.rooms?.[0]?.rates?.[0]?.cancellationPolicies) {
                        selectedRates.forEach((rate, index) => {
                            rate.cancellationPolicies =
                                res?.rooms?.[
                                    index
                                ]?.rates?.[0]?.cancellationPolicies;
                        });
                    } else if (
                        res?.cancellationPolicies?.cancellationPolicies
                    ) {
                        cancellationPolicies =
                            res?.cancellationPolicies?.cancellationPolicies;
                        selectedRates.forEach((rate) => {
                            delete rate.cancellationPolicies;
                        });
                    }

                    selectedRates?.forEach((rate, i) => {
                        rate.supplements = res?.rooms?.[i]?.supplements || [];
                    });

                    setHotelSelection({
                        selectedHotel: hotel,
                        cancellationPolicies: cancellationPolicies,
                        comments: res?.comments || [],
                        seletedRates: selectedRates,
                        checkRateResult: res,
                        totalPrice: res?.totalNet?.total,
                        supplementsArray: [],
                    });
                    navigate(`/hotel-details`);
                })
                .catch((err) => {
                    console.log(err);
                    setError(t("tryAgainLater"));
                    toast.error(t("tryAgainLater"));
                })
                .finally(() => {
                    hideLoading();
                });

            return;
        }
        // create a checkrate result
        const res = {
            rooms: selectedRates.map((rate) => {
                return {
                    rates: [
                        {
                            cancellationPolicies: rate.cancellationPolicies,
                        },
                    ],
                };
            }),
        };

        setHotelSelection({
            selectedHotel: hotel,
            seletedRates: selectedRates,
            checkRateResult: res,
            comments: [],
            cancellationPolicies: [],
            totalPrice: selectedRates.reduce(
                (acc, rate) => acc + rate.price,
                0,
            ),
        });
        navigate(`/hotel-booking`);
    };

    return {
        checkRateNotCombined,
        checkRateCombined,
        goToDetailsCombined,
        goToDetailsNotCombined,
        isLoading,
        error,
    };
};

export default useCheckRate;
